'use client';

import type { ReactNode } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { MetaDefault, MonoDefault } from '../../../../../components/typography';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { DataQueryPanel } from '../data-query/data-query-table';
import {
  parseMcpQueryResult,
  type McpQueryResultRow,
} from './external-network-mcp-query-parse.utils';

function ResultRow({ label, badge }: McpQueryResultRow) {
  return (
    <HStack
      justifyContent="space-between"
      gap={3}
      alignItems="center"
      py={1}
      px={1.5}
      borderRadius="sm"
      width="100%"
    >
      <MonoDefault color="fg.default" truncate flex={1}>
        {label}
      </MonoDefault>
      {badge ? (
        <MetaDefault color="fg.muted" flexShrink={0}>
          {badge}
        </MetaDefault>
      ) : null}
    </HStack>
  );
}

function ResultList({ rows }: { rows: McpQueryResultRow[] }) {
  return (
    <DataQueryPanel>
      <VStack alignItems="stretch" gap={0.5} width="100%" overflowX="hidden">
        {rows.map((row, index) => (
          <ResultRow
            key={`${row.label}:${row.badge ?? ''}:${index}`}
            {...row}
          />
        ))}
      </VStack>
    </DataQueryPanel>
  );
}

function ExternalNetworkMcpQueryCard({
  title,
  status,
  error,
  resultCount,
  rows,
  autoCollapseWhenComplete,
  forceExpanded,
  headerActions,
}: {
  title: string;
  status: string;
  error?: string;
  resultCount?: number;
  rows: McpQueryResultRow[];
  autoCollapseWhenComplete?: boolean;
  forceExpanded?: boolean;
  headerActions?: ReactNode;
}) {
  const t = useTranslations('designSystem.chat.externalNetworkMcpQueryTool');
  const isCompleted = status === 'completed';
  const headerMeta =
    isCompleted && resultCount !== undefined ? (
      <HStack gap={1} flexShrink={0} minWidth={0} alignItems="center">
        <MetaDefault color="fg.muted" flexShrink={0}>
          {t('resultCount', { count: resultCount })}
        </MetaDefault>
        {headerActions}
      </HStack>
    ) : (
      headerActions
    );

  return (
    <InlineExecutionToolCard
      title={title}
      status={status}
      error={error}
      headerMeta={headerMeta}
      defaultExpanded={false}
      autoCollapseWhenComplete={autoCollapseWhenComplete}
      forceExpanded={forceExpanded}
    >
      {rows.length > 0 ? <ResultList rows={rows} /> : null}
    </InlineExecutionToolCard>
  );
}

export function renderExternalNetworkMcpQueryCard(
  ctx: CardRenderContext,
): ReactNode {
  const { toolCallData, toolName } = ctx;
  const isRunning =
    toolCallData.status === 'started' || toolCallData.status === 'in-progress';

  if (
    !isRunning &&
    toolCallData.status !== 'completed' &&
    toolCallData.status !== 'error'
  ) {
    return undefined;
  }

  const result =
    toolCallData.status === 'completed'
      ? parseMcpQueryResult(toolCallData.output)
      : null;

  return (
    <ExternalNetworkMcpQueryCard
      title={toolName}
      status={toolCallData.status}
      error={toolCallData.error}
      resultCount={result?.total}
      rows={result?.rows ?? []}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    />
  );
}
